/**
 * FlowRing - A distinctive visualization showing the flow from purchased to consumed/wasted.
 *
 * This creates a dual-arc ring where:
 * - Outer ring shows total purchased (full circle)
 * - Inner ring shows consumed (green arc) vs wasted (red arc)
 *
 * CSS animations reveal the story of consumption patterns.
 */
import { useEffect, useState, useId } from 'react';
import { Trans } from '@lingui/react/macro';
import { cn } from '@pantry/ui';

interface FlowRingProps {
  purchased: number;
  consumed: number;
  wasted: number;
  size?: number;
  className?: string;
}

export function FlowRing({ purchased, consumed, wasted, size = 200, className }: FlowRingProps) {
  const uniqueId = useId();
  const [animate, setAnimate] = useState(false);

  // Trigger animation after mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Ring geometry
  const outerStrokeWidth = 14;
  const innerStrokeWidth = 10;
  const gap = 8;

  const outerRadius = (size - outerStrokeWidth) / 2;
  const innerRadius = outerRadius - outerStrokeWidth / 2 - gap - innerStrokeWidth / 2;

  const outerCircumference = 2 * Math.PI * outerRadius;
  const innerCircumference = 2 * Math.PI * innerRadius;

  const center = size / 2;

  // Calculate percentages
  const total = consumed + wasted;
  const consumedPercent = total > 0 ? consumed / total : 0;
  const wastedPercent = total > 0 ? wasted / total : 0;
  const wasteRate = total > 0 ? Math.round((wasted / total) * 100) : 0;

  // Calculate stroke dasharray values
  const consumedDashArray = `${innerCircumference * consumedPercent} ${innerCircumference}`;
  const wastedDashArray = `${innerCircumference * wastedPercent} ${innerCircumference}`;

  // Get waste rate color
  const wasteRateColor =
    wasteRate > 30 ? 'text-expired' : wasteRate > 15 ? 'text-expiring-soon' : 'text-fresh';

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <defs>
            <linearGradient id={`outerGradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="1" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.7" />
            </linearGradient>
          </defs>

          {/* Outer ring background track */}
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            stroke="hsl(var(--muted))"
            strokeWidth={outerStrokeWidth}
            fill="transparent"
            opacity={0.5}
          />

          {/* Outer ring progress (purchased) */}
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            stroke={`url(#outerGradient-${uniqueId})`}
            strokeWidth={outerStrokeWidth}
            fill="transparent"
            strokeDasharray={outerCircumference}
            strokeDashoffset={animate ? 0 : outerCircumference}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />

          {/* Inner ring background track */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            stroke="hsl(var(--muted))"
            strokeWidth={innerStrokeWidth}
            fill="transparent"
            opacity={0.3}
          />

          {/* Consumed arc (green) */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            stroke="hsl(var(--fresh))"
            strokeWidth={innerStrokeWidth}
            fill="transparent"
            strokeDasharray={animate ? consumedDashArray : `0 ${innerCircumference}`}
            strokeLinecap="round"
            className="transition-all duration-800 ease-out"
            style={{ transitionDelay: '400ms' }}
          />

          {/* Wasted arc (red) - rotated to start after consumed */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            stroke="hsl(var(--expired))"
            strokeWidth={innerStrokeWidth}
            fill="transparent"
            strokeDasharray={animate ? wastedDashArray : `0 ${innerCircumference}`}
            strokeLinecap="round"
            className="transition-all duration-800 ease-out"
            style={{
              transitionDelay: '600ms',
              transform: `rotate(${consumedPercent * 360}deg)`,
              transformOrigin: 'center',
            }}
          />
        </svg>

        {/* Center content */}
        <div
          className={cn(
            'absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-400',
            animate ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            transitionDelay: '800ms',
            // Constrain center content to inner ring area
            padding: `${outerStrokeWidth + gap + innerStrokeWidth}px`,
          }}
        >
          <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
            <Trans>Waste Rate</Trans>
          </span>
          <span className={cn('font-display text-3xl font-bold leading-tight', wasteRateColor)}>
            {wasteRate}%
          </span>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-fresh" />
              <span className="text-[10px] text-muted-foreground">
                <Trans>Used</Trans>
              </span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-expired" />
              <span className="text-[10px] text-muted-foreground">
                <Trans>Wasted</Trans>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
